import { AlertTriangle, SquareCheckBig } from "lucide-react";

import { Alert } from "../../components/Alert/Alert";
import { Button } from "../../components/Button/Button";
import { Modal } from "../../components/Modal/Modal";
import type { MetadataChangeKind, MetadataDiffRow } from "./metadata";
import styles from "./GuildSaveReviewDialog.module.css";

/** One line of the Settings group: a plain field, shown as it reads on screen. */
export interface SettingsChange {
  /** The field's own name — `name`, `description`, `visibility`. */
  key: string;
  before: string;
  after: string;
}

interface Props {
  /** Named in the title, so the operator sees which guild they are saving. */
  guildName: string;
  /** Changed settings fields; empty when only metadata was touched. */
  settings: SettingsChange[];
  /** Metadata changes, already grouped added → changed → removed. */
  metadata: MetadataDiffRow[];
  /** Metadata keys going up untouched — the PATCH still carries them. */
  unchangedMetadata: number;
  saving: boolean;
  /** Why the last attempt was refused, if it was. Shown here rather than on
   *  the page: the page's alert sits above the cards, far from where the
   *  operator pressed Save. */
  error?: string;
  onCancel: () => void;
  onConfirm: () => void;
}

const TAG_CLASS: Record<MetadataChangeKind, string> = {
  added: styles.tagAdded,
  changed: styles.tagChanged,
  removed: styles.tagRemoved,
};

/**
 * The review step in front of a save that touched `guilds.metadata` — mockup
 * `guild-metadata.html` §5.
 *
 * Metadata is read by the game, and a typo in it is quieter than a broken
 * guild name: nothing on screen looks wrong afterwards. So Save spells out
 * what is about to go up, one key per line. It is the ApplyConfirmModal idiom
 * without the type-to-confirm gate — this edit is reversible; it only has to
 * be noticed.
 *
 * The dialog is neutral until something DISAPPEARS. A removed key is the one
 * irreversible part (nobody stores the old value), so that alone turns the
 * header icon, the alert and the confirm button red.
 */
export function GuildSaveReviewDialog({
  guildName,
  settings,
  metadata,
  unchangedMetadata,
  saving,
  error,
  onCancel,
  onConfirm,
}: Props) {
  const removed = metadata.filter((r) => r.kind === "removed");
  const destructive = removed.length > 0;

  function handleClose() {
    if (saving) return;
    onCancel();
  }

  return (
    <Modal
      open
      onClose={handleClose}
      title={`Review changes to ${guildName}`}
      width="lg"
      icon={
        destructive ? <AlertTriangle size={17} /> : <SquareCheckBig size={17} />
      }
      iconTone={destructive ? "danger" : "neutral"}
      closeDisabled={saving}
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant={destructive ? "danger" : "primary"}
            onClick={onConfirm}
            disabled={saving}
            loading={saving}
          >
            Save changes
          </Button>
        </>
      }
    >
      {error && (
        <div className={styles.warnAlert}>
          <Alert variant="danger" title="Couldn't save changes">
            {error}
          </Alert>
        </div>
      )}

      {destructive && (
        <div className={styles.warnAlert}>
          <Alert variant="warn">
            <b>
              {removed.length === 1
                ? "1 key disappears from "
                : `${removed.length} keys disappear from `}
              <code>metadata</code>.
            </b>{" "}
            The game stops reading{" "}
            {removed.length === 1 ? "it" : "them"} the moment this saves, and the
            previous {removed.length === 1 ? "value is" : "values are"} stored
            nowhere — the only way back is to type{" "}
            {removed.length === 1 ? "it" : "them"} in again.
          </Alert>
        </div>
      )}

      <div className={styles.diffList}>
        {settings.length > 0 && (
          <>
            <div className={styles.diffGroup}>
              Settings · {changeCount(settings.length)}
            </div>
            {settings.map((row) => (
              <div className={styles.diffRow} key={`settings-${row.key}`}>
                <span className={`${styles.diffTag} ${styles.tagChanged}`}>
                  changed
                </span>
                <span className={styles.diffKey}>{row.key}</span>
                <span className={styles.diffVal}>
                  <span className={styles.valOld}>{plain(row.before)}</span>
                  <span className={styles.arrow} aria-hidden>
                    →
                  </span>
                  <span className={styles.valNew}>{plain(row.after)}</span>
                </span>
              </div>
            ))}
          </>
        )}

        {metadata.length > 0 && (
          <>
            <div className={styles.diffGroup}>
              Metadata · {changeCount(metadata.length)}
            </div>
            {metadata.map((row) => (
              <div className={styles.diffRow} key={`metadata-${row.key}`}>
                <span className={`${styles.diffTag} ${TAG_CLASS[row.kind]}`}>
                  {row.kind}
                </span>
                <span className={styles.diffKey}>
                  {row.key}
                  {row.outsideSchema && (
                    <span className={styles.badgeOutside}>outside schema</span>
                  )}
                </span>
                <span className={styles.diffVal}>
                  {row.before !== undefined && (
                    <span className={styles.valOld}>{row.before}</span>
                  )}
                  {row.before !== undefined && row.after !== undefined && (
                    <span className={styles.arrow} aria-hidden>
                      →
                    </span>
                  )}
                  {row.after !== undefined && (
                    <span className={styles.valNew}>{row.after}</span>
                  )}
                </span>
              </div>
            ))}
          </>
        )}
      </div>

      {unchangedMetadata > 0 && !destructive && (
        <div className={styles.helper}>
          The other {unchangedMetadata}{" "}
          {unchangedMetadata === 1 ? "key goes" : "keys go"} up unchanged — the
          PATCH sends the whole object.
        </div>
      )}
    </Modal>
  );
}

function changeCount(n: number): string {
  return `${n} ${n === 1 ? "change" : "changes"}`;
}

/**
 * A Settings value as the dialog prints it: bare, the way it reads on the page
 * — `open → closed`, not `"open" → "closed"`. Only an emptied one is quoted,
 * because "cleared the description" must not render as an arrow into nothing.
 */
function plain(v: string): string {
  return v === "" ? '""' : v;
}
